import { useState } from "react";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { supabase, getTenantHeaders } from "@/integrations/supabase/tenantClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/common/Logo";

const emailSchema = z.string()
  .min(1, "Email is required")
  .email("Please enter a valid email address")
  .max(255, "Email must be less than 255 characters");

interface ForgotPasswordFormProps {
  onSuccess: (email: string) => void;
  onBack: () => void;
}

export function ForgotPasswordForm({ onSuccess, onBack }: ForgotPasswordFormProps) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate email
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setError(result.error.errors[0].message);
      return;
    }

    setLoading(true);

    try {
      const { error: functionError } = await supabase.functions.invoke('send-password-reset-email', {
        body: { email },
        headers: getTenantHeaders(),
      });

      if (functionError) {
        console.error('Password reset function error:', functionError);
      }

      // Always show success to prevent email enumeration
      onSuccess(email);
    } catch (err) {
      // Still show success to prevent enumeration attacks
      onSuccess(email);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full space-y-8">
      {/* Logo and title - left aligned like LoginForm */}
      <div className="flex flex-col items-start space-y-6">
        <Logo size="lg" />
        <div className="text-left space-y-2">
          <h1 className="text-2xl font-realhead tracking-tight text-white">Reset your password</h1>
          <p className="text-gray-400">Enter your email and we'll send you a reset link</p>
        </div>
      </div>

      {/* Form without card wrapper */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-gray-300">Email address</Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoComplete="email"
            autoFocus
            className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
          />
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending link...
            </>
          ) : (
            "Send reset link"
          )}
        </Button>

        <div className="text-center">
          <button
            type="button"
            onClick={onBack}
            className="text-primary hover:underline text-sm"
          >
            Back to sign in
          </button>
        </div>
      </form>
    </div>
  );
}
